
import { CircleAlert, MinusCircle } from 'lucide-react';

import { Pill } from '../../components/ui';
import { seconds, timeLabel } from './format';
import { MediaThumb } from './MediaThumb';
import type { FeedRow } from './feed';
import type { PlacementMedia, PlacementTiming } from './types';
import styles from './placement.module.css';

interface Props {
  rows: FeedRow[];
  timing: PlacementTiming;
  mediaOf: (order: number) => PlacementMedia | null;
  overriddenOrders: ReadonlySet<number>;
  selectedOrder: number | null;
  onSelect: (order: number) => void;
}

export function ScriptFeed({
  rows,
  timing,
  mediaOf,
  overriddenOrders,
  selectedOrder,
  onSelect,
}: Props) {
  return (
    <div className={styles.feed}>
      {rows.map((row) => {
        if (row.kind === 'text') {
          return (
            <div key={row.key} className={`${styles.feedRow} ${styles.feedRowText}`}>
              <div className={`${styles.gutter} ${styles.gutterText}`}>
                №{row.segmentIndex + 1}
              </div>
              <div className={styles.feedBody}>
                <p className={styles.text}>
                  {row.parts.map((part, index) => (
                    <span
                      key={`${row.key}-${index}`}
                      className={part.anchor ? styles.anchor : undefined}
                    >
                      {part.text}
                    </span>
                  ))}
                </p>
              </div>
            </div>
          );
        }

        if (row.kind === 'insert') {
          const item = row.item;
          const media = mediaOf(item.order);
          const overridden = overriddenOrders.has(item.order);
          return (
            <div key={row.key} className={styles.feedRow}>
              <div className={styles.gutter} title={`${item.startTc} – ${item.endTc}`}>
                {timeLabel(item.startMs, item.timing)}
              </div>
              <div className={styles.feedBody}>
                <button
                  type="button"
                  className={`${styles.insertCard} ${
                    selectedOrder === item.order ? styles.insertCardActive : ''
                  }`}
                  onClick={() => onSelect(item.order)}
                >
                  <MediaThumb media={media} className={styles.thumb} />
                  <span className={styles.insertMain}>
                    <span className={styles.insertTop}>
                      <span className={styles.insertName}>
                        {media ? media.title : 'Медиа не подобрано'}
                      </span>
                      {media?.rank !== null && media?.rank !== undefined && (
                        <Pill>№{media.rank}</Pill>
                      )}
                      {overridden && <Pill tone="warn">заменено вручную</Pill>}
                      {media?.shorterThanWindow && <Pill tone="warn">короче окна</Pill>}
                      {item.weak && !overridden && <Pill tone="warn">сомнительно</Pill>}
                      {!row.anchored && (
                        <span title="Фрагмент не нашёлся в тексте дословно. Место привязки смотрите в карточке.">
                          <Pill>место в тексте не подсвечено</Pill>
                        </span>
                      )}
                    </span>
                    <span className={styles.insertWhy}>
                      {item.weak && !overridden
                        ? item.weakReason
                        : media
                          ? `нашлось по запросу «${media.query}»`
                          : `«${item.anchorText}»`}
                    </span>
                  </span>
                  <span className={styles.insertSide}>{seconds(item.durationMs)}</span>
                </button>
              </div>
            </div>
          );
        }

        if (row.kind === 'gap') {
          const gap = row.gap;
          return (
            <div key={row.key} className={styles.feedRow}>
              <div className={styles.gutter}>{timing === 'exact' ? '–' : '≈ –'}</div>
              <div className={styles.feedBody}>
                <div className={styles.gapCard}>
                  <CircleAlert size={15} className={styles.bannerIconDanger} />
                  <div className={styles.gapMain}>
                    <div className={styles.gapTitle}>Материал не нашёлся</div>
                    <div className={styles.gapWhy}>
                      «{gap.anchorText}»: {gap.reason}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        }

        const skip = row.skip;
        return (
          <div key={row.key} className={styles.feedRow}>
            <div className={styles.gutter}>–</div>
            <div className={styles.feedBody}>
              <div className={styles.skipCard}>
                <MinusCircle size={13} strokeWidth={1.5} style={{ flexShrink: 0 }} />
                <span>
                  Убрано правилами плотности: {skip.message}. «{skip.anchorText}»
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
